import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Tooltip } from '@mui/material';
import * as React from 'react';

import { ToneChip } from '../components';
import type { Tone } from '../labels';
import type { IntegrationStatus } from '../integrationTypes';
import { verdict, verdictHint } from './integrationLabels';

const ICONS: Record<string, React.ElementType> = {
    receiving: CheckCircleOutlineIcon,
    polling: CheckCircleOutlineIcon,
    quiet: ScheduleIcon,
    delayed: ScheduleIcon,
    rejecting: BlockIcon,
    failing: ErrorOutlineIcon,
    incomplete: ReportProblemOutlinedIcon,
    not_connected: LinkOffIcon,
    paused: PauseCircleOutlineIcon,
    off: PowerSettingsNewIcon,
    unknown: HelpOutlineIcon,
};

const TEXT_COLOR: Record<Tone, string> = {
    success: 'success.dark',
    error: 'error.main',
    // The theme's warning is a light yellow and even its dark shade is ~3.5:1 on white: the word stays in the
    // text colour, the icon carries the tone (3:1 is enough for an icon).
    warning: 'text.primary',
    info: 'info.main',
    neutral: 'text.secondary',
};

const ICON_COLOR: Record<Tone, string> = { ...TEXT_COLOR, warning: 'warning.dark' };

const hintOf = verdictHint;

/** The card header's verdict: the worst of its accounts', as a chip with text, icon and tone. */
export const VerdictChip = ({ status }: { status: IntegrationStatus }) => {
    const words = verdict(status.verdict);
    const Icon = ICONS[status.verdict] || HelpOutlineIcon;
    return (
        <span data-testid="verdict">
            <ToneChip label={words.label} tone={words.tone} hint={hintOf(status)} icon={<Icon sx={{ fontSize: 16 }} />} />
        </span>
    );
};

/** An account row's verdict: icon and word in its tone, lighter than a chip. */
export const VerdictMark = ({ status }: { status: IntegrationStatus }) => {
    const words = verdict(status.verdict);
    const Icon = ICONS[status.verdict] || HelpOutlineIcon;
    return (
        <Tooltip title={hintOf(status) || ''}>
            <Box
                component="span"
                tabIndex={0}
                data-testid="verdict-mark"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: TEXT_COLOR[words.tone], typography: 'body2', fontWeight: 500, whiteSpace: 'nowrap' }}
            >
                <Icon sx={{ fontSize: 16, color: ICON_COLOR[words.tone] }} aria-hidden />
                {words.label}
            </Box>
        </Tooltip>
    );
};
